/**
 * Cross-matter document copy/paste helpers.
 *
 * Copy stores {id, name} in the app clipboard (document UUID, not the
 * D-prefixed tree key). Paste posts those ids to /documents/copy/.
 */

export const documentIdForCopy = (record) => {
  if (!record || record.type === "folder") return null;
  const uniqueId = record.unique_id;
  if (typeof uniqueId === "string" && uniqueId.startsWith("D") && uniqueId.length > 1) {
    return uniqueId.slice(1);
  }
  const raw = record.id ?? record.document_id ?? null;
  if (raw == null || raw === "") return null;
  const value = String(raw);
  return value.startsWith("D") ? value.slice(1) : value;
};

export const clipboardItemsFromRecords = (records) => {
  const list = Array.isArray(records) ? records : records ? [records] : [];
  const items = [];
  for (const record of list) {
    const id = documentIdForCopy(record);
    if (!id) continue;
    items.push({ id, name: record.name || "" });
  }
  return items;
};

/**
 * Collect every document under a converted tree node (a folder and its
 * nested folders). Empty placeholder children from the tree converter
 * are skipped.
 */
export const collectDocumentsFromTreeNode = (node) => {
  const items = [];
  const visit = (current) => {
    if (!current || typeof current !== "object") return;
    const record = current.record || current;
    const isDocument =
      current.type === "document" ||
      (typeof record.unique_id === "string" && record.unique_id.startsWith("D"));
    if (isDocument && record.type !== "folder") {
      const id = documentIdForCopy(record);
      if (id) {
        items.push({
          id,
          name:
            record.name ||
            (typeof current.title === "string" ? current.title : "") ||
            "",
        });
      }
    }
    const children = Array.isArray(current.children) ? current.children : [];
    children.forEach(visit);
  };
  visit(node);
  return items;
};

export const findTreePasteTarget = (treeData, selectedKeys) => {
  const wanted = new Set((selectedKeys || []).map(String));
  let selectedFolder = null;
  let selectedDocParent = null;
  const visit = (nodes, parentFolder) => {
    for (const node of Array.isArray(nodes) ? nodes : []) {
      if (!node || typeof node !== "object") continue;
      const isSelected = wanted.has(String(node.key));
      if (isSelected && node.type === "folder") selectedFolder = node;
      if (isSelected && node.type === "document") selectedDocParent = parentFolder;
      const nextParent = node.type === "folder" ? node : parentFolder;
      visit(node.children, nextParent);
    }
  };
  visit(treeData, null);
  return selectedFolder || selectedDocParent;
};

export const copySuccessMessage = (items) => {
  if (!items?.length) {
    return "Copied files — open the other matter and click Paste.";
  }
  if (items.length === 1) {
    return `Copied "${items[0].name || "document"}" — open the other matter and click Paste.`;
  }
  return `Copied ${items.length} files — open the other matter and click Paste.`;
};

export const pasteMenuLabel = (count) => {
  const n = Number(count) || 0;
  if (n <= 1) return "Paste";
  return `Paste ${n} files`;
};

export const TREE_CONTEXT_MENU_HOST = "[data-tree-context-menu-host]";

/**
 * antd Tree only puts our right-click menu on the title text. Right-clicking
 * the icon, expander, or indent would otherwise do nothing. Retarget those
 * clicks onto the row's existing Copy/Paste menu.
 */
export const retargetTreeRowContextMenu = (event) => {
  if (!event?.target?.closest) return false;
  if (event.target.closest(TREE_CONTEXT_MENU_HOST)) return false;
  const row = event.target.closest(".ant-tree-treenode");
  const host = row?.querySelector(TREE_CONTEXT_MENU_HOST);
  if (!host) return false;
  event.preventDefault();
  event.stopPropagation();
  host.dispatchEvent(
    new MouseEvent("contextmenu", {
      bubbles: true,
      cancelable: true,
      clientX: event.clientX,
      clientY: event.clientY,
      view: event.view ?? window,
    })
  );
  return true;
};

export const isTreeBackgroundContextMenu = (event) => {
  if (!event?.target?.closest) return false;
  if (event.target.closest(TREE_CONTEXT_MENU_HOST)) return false;
  if (event.target.closest(".ant-tree-treenode")) return false;
  if (event.target.closest("input, textarea, [contenteditable='true']")) {
    return false;
  }
  return true;
};

export const stripFolderPrefix = (id) => {
  if (id == null || id === "") return null;
  const value = String(id);
  return value.startsWith("F") && value.length > 1 ? value.slice(1) : value;
};

export const folderIdForPaste = (record) => {
  if (!record) return null;
  const uniqueId = record.unique_id;
  if (typeof uniqueId === "string" && uniqueId.length > 0) {
    return stripFolderPrefix(uniqueId);
  }
  if (record.id == null || record.id === "") return null;
  return stripFolderPrefix(record.id);
};

export const isTypingTarget = (target) => {
  if (!target) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest?.("[contenteditable='true']"));
};

export const formatPasteNote = ({
  copied = 0,
  failed = 0,
  results = [],
  destinationName = "",
} = {}) => {
  let note = `Pasted ${copied} file${copied === 1 ? "" : "s"}`;
  if (destinationName) note += ` into ${destinationName}`;
  note += ".";
  const renamed = (results || []).filter((row) => row.renamed).length;
  if (renamed) note += ` ${renamed} renamed to avoid conflicts.`;
  if (failed) {
    const firstError = (results || []).find((row) => row.error)?.error || "";
    note += ` ${failed} failed.${firstError ? ` ${firstError}` : ""}`;
  }
  return note;
};

export const extractCopyApiError = (err, fallback = "Paste failed") =>
  err?.response?.data?.error ||
  err?.response?.data?.detail ||
  err?.data?.error ||
  err?.data?.detail ||
  err?.message ||
  fallback;
